import { CmpCheck, loadScript, yieldToMainBackground } from "./utils";
import { createYTPlayer, destroyYTPlayer, getYTPlayer } from "./youtube";

export class MpVideoPlayer {
	constructor() {
		this.show_ads = null;
		this.ready = false;
		this.jwp = document.querySelector('meta[name="video-setup"]')?.content;
		this.plus_user = document.querySelector('meta[name="plus-user"]')?.content;
	}

	async init() {
		return new Promise((resolve, reject) => {
			if (this.plus_user === "true") {
				this.show_ads = false;
				this.ready = true;
				resolve(this.ready);
			} else {
				const checkCmp = new CmpCheck();
				checkCmp.checkConsent().then((response) => {
					this.show_ads = response;
					this.ready = true;
					resolve(this.ready);
				});
			}
		});
	}

	// Setup JW Player
	setupPlayer(
		id,
		video_id,
		{
			is_youtube = false,
			no_ads = undefined,
			autostart = false,
			article_url = undefined,
			is_downloadable = undefined,
			width = undefined,
			height = undefined,
		} = {},
	) {
		return new Promise((resolve, reject) => {
			if (is_youtube === "true") {
				createYTPlayer(id, {
					videoId: video_id,
					width: width || "100%",
					height: height || "100%",
					playerVars: { playsinline: 1 },
					events: {
						onReady: onPlayerReady,
						onStateChange: onPlayerStateChange,
						onError: onPlayerError,
					},
				}).then((player) => {
					// console.log("ready", player);
					resolve(player);
				});

				function onPlayerReady(event) {
					const ytEvent = new Event("YTplayerReady");
					document.dispatchEvent(ytEvent);
				}
				function onPlayerStateChange(event) {
					// console.log("player changed", event);
				}
				function onPlayerError(event) {
					console.error("YouTube Player Error:", event);
				}
			} else {
				// console.log("Setting up JW Player");
				loadScript(
					"jwplayer",
					`https://content.jwplatform.com/libraries/${this.jwp}.js`,
				).then(() => {
					const config = {
						playlist: `https://cdn.jwplayer.com/v2/media/${video_id}`,
						autostart: autostart,
						skin: {
							name: "multi",
						},
					};

					if (width && height) {
						config.width = width;
						config.height = height;
					}
					if (no_ads === "true" || this.show_ads === false) {
						config.advertising = {};
					}
					// console.log(config);
					const player = jwplayer(id).setup(config);

					if (is_downloadable === "true") {
						// download button
						player.on("play", function (event) {
							player.addButton(
								//This portion is what designates the graphic used for the button
								"/static/img/download.svg",
								//This portion determines the text that appears as a tooltip
								"Download Video",
								//This portion designates the functionality of the button itself
								() => {
									const levels = player.getQualityLevels();
									const quality = player.getCurrentQuality();
									const quality_settings = levels[quality];
									let dw_label;
									if (quality_settings.label.toLowerCase() === "auto") {
										dw_label = "720";
									} else {
										dw_label = quality_settings.label.substring(
											0,
											quality_settings.label.length - 1,
										);
									}
									let source = "";
									player.getPlaylistItem().allSources.map((s) =>
										// biome-ignore format lint: reason=
										s.height == dw_label ? (source = s.file) : null,
									);
									window.open(source, "_blank");
								},
								//And finally, here we set the unique ID of the button itself
								"download",
							);
						});
					}

					if (article_url) {
						// comments button
						player.on("play", function (event) {
							this.addButton(
								//This portion is what designates the graphic used for the button
								"/static/img/commenti.svg",
								//This portion determines the text that appears as a tooltip
								"Commenta",
								//This portion designates the functionality of the button itself
								() => {
									window.location.href = article_url;
								},
								//And finally, here we set the unique ID of the button itself
								"comments",
							);
						});
					}

					resolve(player);
				});
			}
		});
	}

	getPlayer(video_id, is_youtube = false) {
		if (this.normalizeBoolean(is_youtube)) {
			return getYTPlayer(video_id);
		}
		return jwplayer(video_id);
	}

	destroyPlayer(video_id) {
		if (
			this.normalizeBoolean(
				document.getElementById(video_id).dataset.is_youtube,
			)
		) {
			destroyYTPlayer(video_id);
		} else {
			const current_player = jwplayer(video_id);
			if (Object.prototype.hasOwnProperty.call(current_player, "remove")) {
				current_player.remove();
			}
		}
	}

	normalizeBoolean(value) {
		return value === "true" || value === true;
	}
}

// Display video player in a lightbox
function ajax_lightbox_player(element, video_container, multiJWP) {
	fetch(element.dataset.href, {
		headers: {
			"X-Requested-With": "XMLHttpRequest",
		},
	})
		.then((response) => response.text())
		.then(async (data) => {
			video_container.innerHTML = data;
			const video_player = video_container.querySelector("._player-video");
			const video_id = video_player.id;
			const is_youtube = multiJWP.normalizeBoolean(
				video_player.dataset.is_youtube,
			);
			const player = await multiJWP.setupPlayer(
				video_id,
				video_player.dataset.video_uid,
				{
					noads: video_player.dataset.noads,
					autostart: video_player.dataset.autostart,
					is_youtube: video_player.dataset.is_youtube,
				},
			);

			import("basiclightbox").then((basicLightbox) => {
				const lightbox = basicLightbox.create(video_container, {
					className: "basicLightbox--video-content",
					onShow: (instance) => {
						// Play the video when the lightbox is shown
						if (is_youtube) {
							document.addEventListener("YTplayerReady", () => {
								player.playVideo();
							});
						} else {
							player.play();
						}
					},
				});
				lightbox.show();
			});
		})
		.catch((error) => {
			console.warn(error);
		});
}

function ready(fn) {
	if (document.readyState !== "loading") {
		fn();
	} else {
		document.addEventListener("DOMContentLoaded", fn);
	}
}

ready(() => {
	const multiJWP = new MpVideoPlayer();
	multiJWP.init().then((is_ready) => {
		if (!is_ready) {
			return;
		}

		document.body.addEventListener("click", (event) => {
			// video con lightbox
			if (event.target?.classList.contains("_lightbox_video")) {
				event.preventDefault();
				yieldToMainBackground().then(() => {
					ajax_lightbox_player(
						event.target,
						event.target.nextElementSibling,
						multiJWP,
					);
				});
			}
		});

		// video in page
		for (const el of document.querySelectorAll("._player-video")) {
			yieldToMainBackground().then(() => {
				const video_id = el.id;
				const video_uid = el.dataset.video_uid;
				const is_youtube = el.dataset.is_youtube;
				const noads = el.dataset.noads;
				const autostart = el.dataset.autostart;
				const comments_url = el.dataset.comments_url;
				const is_downloadable = el.dataset.is_downloadable;
				const width = el.dataset.width;
				const height = el.dataset.height;
				multiJWP.setupPlayer(video_id, video_uid, {
					noads: noads,
					autostart: autostart,
					article_url: comments_url,
					is_downloadable: is_downloadable,
					width: width,
					height: height,
					is_youtube: is_youtube,
				});
			});
		}
	});
});
